import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Edit, Trash2, Package, Upload, GitMerge, Download, Paperclip } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import BulkImportDialog from "@/components/BulkImportDialog";
import ComponentInvoicesDialog from "@/components/ComponentInvoicesDialog";
import ComponentInvoiceField from "@/components/ComponentInvoiceField";
import { fetchInvoiceCounts as fetchInvoiceCountsApi, uploadInvoice } from "@/lib/invoices";
import { getRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  name: "",
  sku: "",
  description: "",
  quantity: 0,
  unit_cost: 0,
  min_stock_level: 10,
  supplier_name: "",
  supplier_contact: "",
  category: "",
  unit: "pcs",
};

export default function Components() {
  const [components, setComponents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [componentToDelete, setComponentToDelete] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // Merge state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergePending, setMergePending] = useState(false);
  // 409 collision merge proposal
  const [collisionDialogOpen, setCollisionDialogOpen] = useState(false);
  const [collisionExisting, setCollisionExisting] = useState(null);
  const [collisionIncoming, setCollisionIncoming] = useState(null);
  // Bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  // Invoice attachments
  const [invoiceCounts, setInvoiceCounts] = useState({});
  const [invoiceComponent, setInvoiceComponent] = useState(null);
  const [invoicesDialogOpen, setInvoicesDialogOpen] = useState(false);
  const [stagedInvoiceFiles, setStagedInvoiceFiles] = useState([]);
  const isViewer = getRole() === "viewer";

  useEffect(() => {
    fetchComponents();
    fetchCategories();
    fetchInvoiceCounts();
  }, [search, selectedCategory]);

  const fetchComponents = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedCategory) params.append("category", selectedCategory);

      const response = await axios.get(`${API}/components?${params}`);
      setComponents(response.data);
    } catch (error) {
      console.error("Error fetching components:", error);
      toast.error("Failed to fetch components");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/components/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchInvoiceCounts = async () => {
    try {
      setInvoiceCounts(await fetchInvoiceCountsApi());
    } catch (error) {
      console.error("Error fetching invoice counts:", error);
    }
  };

  // Uploads the files picked in the create dialog, once the component exists.
  const uploadStagedInvoices = async (componentId) => {
    if (!stagedInvoiceFiles.length) return;
    let uploaded = 0;
    for (const file of stagedInvoiceFiles) {
      try {
        await uploadInvoice(componentId, file);
        uploaded += 1;
      } catch (error) {
        console.error("Error uploading invoice:", error);
        toast.error(
          `${file.name}: ${error.response?.data?.detail || "upload failed"}`
        );
      }
    }
    setStagedInvoiceFiles([]);
    if (uploaded) {
      toast.success(uploaded === 1 ? "Invoice attached" : `${uploaded} invoices attached`);
    }
    await fetchInvoiceCounts();
  };

  const openInvoicesDialog = (component) => {
    setInvoiceComponent(component);
    setInvoicesDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingComponent) {
        await axios.put(`${API}/components/${editingComponent.id}`, formData);
        toast.success("Component updated successfully");
      } else {
        const created = await axios.post(`${API}/components`, formData);
        toast.success("Component created successfully");
        await uploadStagedInvoices(created.data.id);
      }
      setDialogOpen(false);
      setEditingComponent(null);
      setFormData(initialFormData);
      setStagedInvoiceFiles([]);
      fetchComponents();
      fetchCategories();
      fetchInvoiceCounts();
    } catch (error) {
      if (!editingComponent && error.response?.status === 409) {
        // SKU collision — offer to merge
        setCollisionExisting(error.response.data.detail.existing);
        setCollisionIncoming({ ...formData });
        setDialogOpen(false);
        setCollisionDialogOpen(true);
        return;
      }
      console.error("Error saving component:", error);
      toast.error(error.response?.data?.detail || "Failed to save component");
    }
  };

  const handleEdit = (component) => {
    setEditingComponent(component);
    setStagedInvoiceFiles([]);
    setFormData({
      name: component.name,
      sku: component.sku,
      description: component.description || "",
      quantity: component.quantity,
      unit_cost: component.unit_cost,
      min_stock_level: component.min_stock_level,
      supplier_name: component.supplier_name || "",
      supplier_contact: component.supplier_contact || "",
      category: component.category || "",
      unit: component.unit || "pcs",
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!componentToDelete) return;
    try {
      await axios.delete(`${API}/components/${componentToDelete.id}`);
      toast.success("Component deleted successfully");
      setDeleteDialogOpen(false);
      setComponentToDelete(null);
      fetchComponents();
    } catch (error) {
      console.error("Error deleting component:", error);
      toast.error("Failed to delete component");
    }
  };

  const openNewDialog = () => {
    setEditingComponent(null);
    setFormData(initialFormData);
    setStagedInvoiceFiles([]);
    setDialogOpen(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === components.length && components.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(components.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.delete(`${API}/components/bulk`, { data: { ids: [...selectedIds] } });
      toast.success(`Deleted ${selectedIds.size} component(s)`);
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      fetchComponents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleCollisionMerge = async () => {
    try {
      // Create the incoming component first, then merge it into the existing one
      const created = await axios.post(`${API}/components`, { ...collisionIncoming, sku: collisionIncoming.sku + "__tmp_" + Date.now() });
      await axios.post(`${API}/components/merge`, { source_id: created.data.id, target_id: collisionExisting.id });
      toast.success(`Merged into existing component "${collisionExisting.name}"`);
      // Invoices picked in the create dialog now belong to the surviving component
      await uploadStagedInvoices(collisionExisting.id);
    } catch (err) {
      toast.error("Merge failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setCollisionDialogOpen(false);
      setCollisionExisting(null);
      setCollisionIncoming(null);
      fetchComponents();
    }
  };

  const handleExport = async (fmt) => {
    try {
      const res = await axios.get(`${API}/export/components?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `components.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleMergeComponents = async () => {
    if (!mergeSourceId || !mergeTargetId) return;
    setMergePending(true);
    try {
      await axios.post(`${API}/components/merge`, { source_id: mergeSourceId, target_id: mergeTargetId });
      toast.success("Components merged successfully");
      setMergeDialogOpen(false);
      setMergeSourceId("");
      setMergeTargetId("");
      fetchComponents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally {
      setMergePending(false);
    }
  };

  const mergePreview = () => {
    if (!mergeSourceId || !mergeTargetId) return null;
    const src = components.find(c => c.id === mergeSourceId);
    const tgt = components.find(c => c.id === mergeTargetId);
    if (!src || !tgt) return null;
    return {
      name: tgt.name,
      sku: tgt.sku,
      quantity: (tgt.quantity || 0) + (src.quantity || 0),
      unit_cost: tgt.unit_cost || src.unit_cost,
      supplier_name: tgt.supplier_name || src.supplier_name || "-",
      category: tgt.category || src.category || "-",
    };
  };

  const getStockStatus = (component) => {
    if (component.quantity === 0) return "critical";
    if (component.quantity <= component.min_stock_level) return "low";
    return "ok";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  return (
    <div data-testid="components-page">
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Components</h1>
          <p className="page-subtitle">Manage individual parts and materials</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.size})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")}>CSV (.csv)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" onClick={() => { setMergeSourceId(""); setMergeTargetId(""); setMergeDialogOpen(true); }} className="rounded-none uppercase text-xs tracking-wider">
            <GitMerge className="w-4 h-4 mr-2" />
            Merge
          </Button>
          <Button onClick={openNewDialog} className="rounded-none uppercase text-xs tracking-wider" data-testid="add-component-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Component
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="search-input"
            />
          </div>
          <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-[200px] rounded-none" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Components Table */}
        <Card className="rounded-none border-zinc-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading components...</div>
            ) : components.length === 0 ? (
              <div className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No components found</p>
                <Button onClick={openNewDialog} variant="outline" className="mt-4 rounded-none">
                  Add your first component
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="components-table">
                  <thead>
                    <tr>
                      <th className="w-8">
                        <Checkbox
                          checked={selectedIds.size === components.length && components.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th>SKU</th>
                      <th>Name</th>
                      <th className="hidden lg:table-cell">Category</th>
                      <th className="text-right whitespace-nowrap">Quantity</th>
                      <th className="text-right whitespace-nowrap">Unit Cost</th>
                      <th className="text-right whitespace-nowrap hidden 2xl:table-cell">Total Value</th>
                      <th className="hidden xl:table-cell">Supplier</th>
                      <th className="whitespace-nowrap">Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((component) => {
                      const status = getStockStatus(component);
                      return (
                        <tr key={component.id} data-testid={`component-row-${component.id}`}>
                          <td>
                            <Checkbox
                              checked={selectedIds.has(component.id)}
                              onCheckedChange={() => toggleSelect(component.id)}
                            />
                          </td>
                          <td>
                            <span className="sku-badge">{component.sku}</span>
                          </td>
                          <td className="font-sans font-medium">{component.name}</td>
                          <td className="hidden lg:table-cell">{component.category || "-"}</td>
                          <td className="text-right whitespace-nowrap">
                            <span className={`quantity-display ${status}`}>
                              {component.quantity} {component.unit}
                            </span>
                          </td>
                          <td className="text-right cost-display whitespace-nowrap">{formatCurrency(component.unit_cost)}</td>
                          <td className="text-right cost-display whitespace-nowrap hidden 2xl:table-cell">{formatCurrency(component.quantity * component.unit_cost)}</td>
                          <td className="hidden xl:table-cell">{component.supplier_name || "-"}</td>
                          <td className="whitespace-nowrap">
                            <Badge
                              variant={status === "critical" ? "destructive" : status === "low" ? "outline" : "secondary"}
                              className={`rounded-none uppercase text-xs ${status === "low" ? "border-orange-500 text-orange-500" : ""}`}
                            >
                              {status === "critical" ? "OUT OF STOCK" : status === "low" ? "LOW STOCK" : "IN STOCK"}
                            </Badge>
                          </td>
                          <td className="text-right">
                            <div className="flex justify-end gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => openInvoicesDialog(component)}
                                title="Invoices"
                                data-testid={`invoices-component-${component.id}`}
                              >
                                <span className="relative inline-flex">
                                  <Paperclip className="w-3.5 h-3.5" />
                                  {invoiceCounts[component.id] > 0 && (
                                    <span className="absolute -top-1.5 -right-2 min-w-[14px] h-3.5 px-0.5 rounded-full bg-primary text-primary-foreground text-[9px] leading-[14px] text-center font-mono">
                                      {invoiceCounts[component.id]}
                                    </span>
                                  )}
                                </span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleEdit(component)}
                                data-testid={`edit-component-${component.id}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setComponentToDelete(component);
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`delete-component-${component.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="component-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              {editingComponent ? "Edit Component" : "Add Component"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6 py-4">
              {/* Basic Info */}
              <div className="form-section">
                <div className="form-section-header">Basic Information</div>
                <div className="form-section-content grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="rounded-none"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku">SKU *</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                      required
                      placeholder="CMP-[CAT]-001"
                      className="rounded-none font-mono"
                      data-testid="input-sku"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-none"
                      rows={2}
                      data-testid="input-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="rounded-none"
                      placeholder="e.g., Electronics, Mechanical"
                      data-testid="input-category"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit">Unit</Label>
                    <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                      <SelectTrigger className="rounded-none" data-testid="input-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pcs">Pieces (pcs)</SelectItem>
                        <SelectItem value="kg">Kilograms (kg)</SelectItem>
                        <SelectItem value="m">Meters (m)</SelectItem>
                        <SelectItem value="l">Liters (l)</SelectItem>
                        <SelectItem value="box">Boxes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Stock & Pricing */}
              <div className="form-section">
                <div className="form-section-header">Stock & Pricing</div>
                <div className="form-section-content grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-quantity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_cost">Unit Cost ($)</Label>
                    <Input
                      id="unit_cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-unit-cost"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock_level">Min Stock Level</Label>
                    <Input
                      id="min_stock_level"
                      type="number"
                      min="0"
                      value={formData.min_stock_level}
                      onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-min-stock"
                    />
                  </div>
                </div>
              </div>

              {/* Supplier Info */}
              <div className="form-section">
                <div className="form-section-header">Supplier Information</div>
                <div className="form-section-content grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="supplier_name">Supplier Name</Label>
                    <Input
                      id="supplier_name"
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      className="rounded-none"
                      data-testid="input-supplier-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier_contact">Supplier Contact</Label>
                    <Input
                      id="supplier_contact"
                      value={formData.supplier_contact}
                      onChange={(e) => setFormData({ ...formData, supplier_contact: e.target.value })}
                      className="rounded-none"
                      placeholder="Email or phone"
                      data-testid="input-supplier-contact"
                    />
                  </div>
                </div>
              </div>
              {/* Invoices */}
              <div className="form-section">
                <div className="form-section-header">Invoices</div>
                <div className="form-section-content">
                  <ComponentInvoiceField
                    componentId={editingComponent?.id}
                    stagedFiles={stagedInvoiceFiles}
                    onStagedFilesChange={setStagedInvoiceFiles}
                    onChanged={fetchInvoiceCounts}
                    readOnly={isViewer}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none uppercase tracking-wider" data-testid="save-component-btn">
                {editingComponent ? "Update" : "Create"} Component
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Component(s)</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} selected component(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="rounded-none bg-destructive">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Component</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{componentToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-destructive" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        type="components"
        onSuccess={() => { fetchComponents(); fetchCategories(); }}
      />

      {/* Invoice Attachments Dialog */}
      <ComponentInvoicesDialog
        open={invoicesDialogOpen}
        onOpenChange={setInvoicesDialogOpen}
        component={invoiceComponent}
        readOnly={isViewer}
        onChanged={fetchInvoiceCounts}
      />

      {/* SKU Collision Merge Dialog */}
      <AlertDialog open={collisionDialogOpen} onOpenChange={setCollisionDialogOpen}>
        <AlertDialogContent className="rounded-none max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate SKU — Merge?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>A component with SKU <span className="font-mono font-bold">{collisionExisting?.sku}</span> already exists.</p>
                <div className="grid grid-cols-2 gap-4 border border-border p-3 text-xs">
                  <div>
                    <p className="font-semibold text-muted-foreground uppercase tracking-wider mb-1">Existing</p>
                    <p className="font-medium">{collisionExisting?.name}</p>
                    <p>Qty: <span className="font-mono">{collisionExisting?.quantity}</span></p>
                    <p>Cost: <span className="font-mono">{collisionExisting?.unit_cost}</span></p>
                    <p>Supplier: {collisionExisting?.supplier_name || "-"}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-muted-foreground uppercase tracking-wider mb-1">Incoming</p>
                    <p className="font-medium">{collisionIncoming?.name}</p>
                    <p>Qty: <span className="font-mono">{collisionIncoming?.quantity}</span></p>
                    <p>Cost: <span className="font-mono">{collisionIncoming?.unit_cost}</span></p>
                    <p>Supplier: {collisionIncoming?.supplier_name || "-"}</p>
                  </div>
                </div>
                <p className="text-muted-foreground">Merging will <strong>add quantities</strong> and fill any blank fields in the existing record from the incoming data. The existing component is kept; no duplicate is created.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" onClick={() => { setCollisionDialogOpen(false); setDialogOpen(true); }}>Back to Edit</AlertDialogCancel>
            <AlertDialogAction onClick={handleCollisionMerge} className="rounded-none">Merge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">Merge Components</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">The <strong>source</strong> component will be merged into the <strong>target</strong> and then deleted. Quantities are added together.</p>
            <div className="space-y-2">
              <Label>Source (will be deleted)</Label>
              <Select value={mergeSourceId} onValueChange={setMergeSourceId}>
                <SelectTrigger className="rounded-none">
                  <SelectValue placeholder="Select source component..." />
                </SelectTrigger>
                <SelectContent>
                  {components.filter(c => c.id !== mergeTargetId).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="font-mono text-xs mr-2">{c.sku}</span>{c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target (will be kept)</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger className="rounded-none">
                  <SelectValue placeholder="Select target component..." />
                </SelectTrigger>
                <SelectContent>
                  {components.filter(c => c.id !== mergeSourceId).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="font-mono text-xs mr-2">{c.sku}</span>{c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mergePreview() && (
              <div className="border border-border p-3 text-xs space-y-1 bg-muted/30">
                <p className="font-semibold uppercase tracking-wider text-muted-foreground mb-2">Merge Preview</p>
                <p><span className="text-muted-foreground w-24 inline-block">Name:</span> {mergePreview().name}</p>
                <p><span className="text-muted-foreground w-24 inline-block">SKU:</span> <span className="font-mono">{mergePreview().sku}</span></p>
                <p><span className="text-muted-foreground w-24 inline-block">Quantity:</span> <span className="font-mono font-bold">{mergePreview().quantity}</span></p>
                <p><span className="text-muted-foreground w-24 inline-block">Unit Cost:</span> <span className="font-mono">{formatCurrency(mergePreview().unit_cost)}</span></p>
                <p><span className="text-muted-foreground w-24 inline-block">Supplier:</span> {mergePreview().supplier_name}</p>
                <p><span className="text-muted-foreground w-24 inline-block">Category:</span> {mergePreview().category}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)} className="rounded-none">Cancel</Button>
            <Button
              onClick={handleMergeComponents}
              disabled={!mergeSourceId || !mergeTargetId || mergePending}
              className="rounded-none uppercase tracking-wider"
            >
              <GitMerge className="w-4 h-4 mr-2" />
              {mergePending ? "Merging..." : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
